import React, { Suspense, lazy, useMemo, useEffect } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { Redirect } from "react-router-dom";
import { ContentRoute } from "../_metronic/layout";
import { actions } from "./modules/Auth/_redux/authRedux";
import * as leftMenuActions from "./modules/Hivee/_redux/leftMenu/leftMenuActions";
import CacheRoute, { CacheSwitch } from "react-router-cache-route";

import * as groupAction from "./modules/Hivee/_redux/groups/groupsActions";
import * as djActions from "./modules/Hivee/_redux/djs/djsActions";
import * as musicStyleActions from "./modules/Hivee/_redux/musicstyles/musicstylesActions";
import * as partnerActions from "./modules/Hivee/_redux/partner/partnerActions";

import "./modules/Hivee/components";
import { flattenRoutes } from "./modules/Hivee/_helpers/routerHelper";
import { getInitialData } from "./modules/Auth/_redux/authCrud";

export default function BasePage() {
  const { leftMenus, menuLoading } = useSelector(
    (state) => state.leftMenu,
    shallowEqual
  );

  const dispatch = useDispatch();

  // 只等菜单加载完成，dictList 等在后台并行加载不阻塞渲染
  useEffect(() => {
    dispatch(groupAction.getRoleList());
    dispatch(musicStyleActions.getStyleSelect());
    dispatch(djActions.getDjSelect());
    dispatch(partnerActions.getPartnerSelect());
    dispatch(leftMenuActions.getLeftMenu());
    getInitialData()
      .then(([dictList]) => {
        dispatch(actions.fulfillDictList(dictList));
      })
      .catch((err) => {
        console.warn("Failed to load initial data, continuing without it:", err);
      });
  }, []);

  // 用 useMemo 同步派生，避免 useState+useEffect 的时序竞争
  const homepage = useMemo(() => {
    if (!leftMenus || leftMenus.length === 0) return "/";
    let path = leftMenus[0].path;
    if (leftMenus[0].children && leftMenus[0].children.length > 0 && !leftMenus[0].children[0]?.hide) {
      path = path + leftMenus[0].children[0].path;
    }
    return path;
  }, [leftMenus]);

  // 每个路由组件用独立 Suspense 包裹，避免 CacheRoute 与 React.lazy 的兼容问题
  // lazy() 引用固定在 useMemo 中，leftMenus 不变则不重建
  const routeComponents = useMemo(() => {
    if (!leftMenus || leftMenus.length === 0) return {};
    const map = {};
    flattenRoutes(leftMenus).forEach((route) => {
      if (route.componentPath && !map[route.componentPath]) {
        const LazyComp = lazy(() => import(`./pages/${route.componentPath}`));
        function RouteWrapper(props) {
          return (
            <Suspense fallback={
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", border: "4px solid #eee", borderTopColor: "#3699FF", animation: "spin 0.8s linear infinite" }} />
              </div>
            }>
              <LazyComp {...props} />
            </Suspense>
          );
        }
        map[route.componentPath] = RouteWrapper;
      }
    });
    return map;
  }, [leftMenus]);

  if (menuLoading == undefined || menuLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          border: "4px solid #eee", borderTopColor: "#3699FF",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <CacheSwitch>
      <Redirect exact from="/" to={homepage} />
      {flattenRoutes(leftMenus || []).map((route, index) => {
        const RouteComponent = routeComponents[route.componentPath];
        if (!RouteComponent) return null;
        if (route.name == "generalTicketList" || route.name == "vipTableList") {
          return (
            <CacheRoute
              key={`route-${index}`}
              path={route.path}
              exact
              component={RouteComponent}
            />
          );
        }
        if (route.hide) {
          return (
            <ContentRoute
              key={`route-${index}`}
              path={route.path}
              exact
              component={RouteComponent}
            />
          );
        }
        return (
          <CacheRoute
            key={`route-${index}`}
            path={route.path}
            exact
            component={RouteComponent}
          />
        );
      })}
      <Redirect to="/error/error-v1" />
    </CacheSwitch>
    </>
  );
}
